/**
 * Sections table + section editor in a modal. Submit targets parent form via `form` attribute.
 */

import { Fragment, useState, useEffect } from 'react'
import {
  useFieldArray,
  Controller,
  useWatch,
} from 'react-hook-form'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
  deleteEntityActionButtonClass,
} from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import parityStyles from '@/features/invitee/styles/invitesParity.module.css'
import type {
  CheckboxTemplateChildRow,
  CheckboxTemplateMasterDetailProps,
  CheckboxTemplateSectionDetailPanelProps,
} from '../types'
import { stripHtmlToPreview } from '../utils/templatePreview'

export type { CheckboxTemplateMasterMode } from '../types'

function newMissingChild(): CheckboxTemplateChildRow {
  return {
    key: '',
    label: '',
    description: '',
    hasInput: false,
    isLock: 0,
  }
}

function newDeniedChild(): CheckboxTemplateChildRow {
  return {
    key: '',
    label: '',
    description: '',
    hasInput: false,
    isLock: 0,
  }
}

export function CheckboxTemplateMasterDetail({
  mode,
  control,
  register,
  errors,
  sectionFields,
  onAddSection,
  onRemoveSection,
  sectionModalIndex,
  setSectionModalIndex,
  onDismissSectionModal,
  parentFormId,
  submitLabel,
  submitLoading,
}: CheckboxTemplateMasterDetailProps) {
  const watchedSections = useWatch({ control, name: 'checkbox_data' })

  const handleRemoveSection = (index: number) => {
    onRemoveSection(index)
  }

  const handleAddSection = () => {
    onAddSection()
  }

  const headingTitle = mode === 'missingInfo' ? 'Missing Info' : 'Denied Reason'
  const introLabel = 'Email Checkbox Section'

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-slate-900">{headingTitle}</h2>
        <p className="text-sm font-medium text-slate-700">{introLabel}</p>
      </div>

      <div
        className={parityStyles.tableCard}
        role="region"
        aria-label="Sections list"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50">
          <span className="text-sm font-medium text-slate-800">Sections</span>
          <button
            type="button"
            onClick={handleAddSection}
            className={cn(primaryEntityActionButtonClass, 'h-9 gap-1.5 px-3.5 whitespace-nowrap')}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Add New Section
          </button>
        </div>

        {sectionFields.length === 0 ? (
          <p className="text-sm text-slate-500 p-4">
            No sections yet. Click &quot;Add New Section&quot; to create one.
          </p>
        ) : (
          <div className={cn(parityStyles.tableResponsiveScroll, 'touch-scroll')}>
            <table className={cn(parityStyles.listingTable, parityStyles.propertyListingTable, 'table-fixed w-full min-w-[50rem] !max-w-none [&_thead_th]:!whitespace-nowrap [&_tbody_td:not([colspan])]:!whitespace-normal [&_tbody_td:not([colspan])]:break-words [&_thead_th]:!px-2.5 [&_thead_th]:!py-2 [&_thead_th]:!align-middle [&_tbody_td]:!px-2.5 [&_tbody_td]:!py-2 [&_tbody_td]:!text-[13px] [&_tbody_td]:!leading-snug')} role="grid">
              <thead>
                <tr>
                  <th scope="col" className="!text-left">Key</th>
                  <th scope="col" className="!text-left">Label</th>
                  <th scope="col" className="!text-center">Items</th>
                  <th scope="col" className="!text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {sectionFields.map((row, i) => {
                  const section = watchedSections?.[i]
                  const preview = stripHtmlToPreview(section?.label)
                  const nChild = section?.child?.length ?? 0
                  const modalOpen = sectionModalIndex === i
                  return (
                    <tr
                      key={row.id}
                      role="row"
                      aria-selected={modalOpen}
                      data-testid={`underwriting-master-row-${i}`}
                      className={cn(
                        'cursor-pointer transition-colors hover:bg-slate-50/50',
                        modalOpen && 'bg-button-soft'
                      )}
                      onClick={() => setSectionModalIndex(i)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setSectionModalIndex(i)
                        }
                      }}
                      tabIndex={0}
                    >
                      <td className="align-top font-mono text-xs text-slate-900">
                        {section?.key || '—'}
                      </td>
                      <td className="align-top text-slate-600 max-w-[200px] truncate">
                        {preview}
                      </td>
                      <td className="!text-center text-slate-700">{nChild}</td>
                      <td className="!text-center">
                        <button
                          type="button"
                          className={cn(
                            deleteEntityActionButtonClass,
                            'h-8 min-w-0 gap-1.5 px-3 text-xs',
                          )}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleRemoveSection(i)
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          Remove Section
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OnScreenModal
        isOpen={sectionModalIndex !== null}
        title={
          sectionModalIndex !== null ? `Section ${sectionModalIndex + 1}` : 'Section'
        }
        onClose={onDismissSectionModal}
        maxWidth="max-w-3xl"
        overlayDataTestId="underwriting-section-modal-overlay"
        panelDataTestId="underwriting-section-modal-panel"
      >
        {sectionModalIndex !== null && (
          <>
            <SectionDetailPanel
              mode={mode}
              sectionIndex={sectionModalIndex}
              control={control}
              register={register}
              errors={errors}
              newChild={mode === 'missingInfo' ? newMissingChild : newDeniedChild}
            />
            <div className="flex flex-wrap justify-center gap-2 pt-4 mt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={onDismissSectionModal}
                className={secondaryEntityActionButtonClass}
              >
                Cancel
              </button>
              <button
                type="submit"
                form={parentFormId}
                disabled={submitLoading}
                className={primaryEntityActionButtonClass}
              >
                {submitLabel}
              </button>
            </div>
          </>
        )}
      </OnScreenModal>
    </div>
  )
}

function SectionDetailPanel({
  mode,
  sectionIndex,
  control,
  register,
  errors,
  newChild,
}: CheckboxTemplateSectionDetailPanelProps) {
  const fieldName = `checkbox_data.${sectionIndex}` as const
  const sectionErrors = errors.checkbox_data?.[sectionIndex]
  const [expandedChildIndex, setExpandedChildIndex] = useState<number | null>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: collapse the expanded child when the section changes
    setExpandedChildIndex(null)
  }, [sectionIndex])

  const {
    fields: childFields,
    append: appendChild,
    remove: removeChild,
  } = useFieldArray({ control, name: `${fieldName}.child` })

  const toggleExpand = (childIndex: number) => {
    setExpandedChildIndex((prev) => (prev === childIndex ? null : childIndex))
  }

  const handleRemoveChild = (childIndex: number) => {
    removeChild(childIndex)
    setExpandedChildIndex((prev) => {
      if (prev === null) return null
      if (prev === childIndex) return null
      if (prev > childIndex) return prev - 1
      return prev
    })
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Key</label>
        <input
          type="text"
          placeholder="Key"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-button/30"
          {...register(`${fieldName}.key`)}
        />
        {sectionErrors?.key && (
          <p className="text-xs text-red-600 mt-1">{sectionErrors.key.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Label</label>
        <Controller
          name={`${fieldName}.label`}
          control={control}
          render={({ field: { value, onChange } }) => (
            <CKEditor4 id={`${fieldName}.label`} value={value || ''} onChange={onChange} />
          )}
        />
      </div>

      <div className="border-t border-slate-200 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-sm font-medium text-slate-800">Sub-sections</span>
          {childFields.length === 0 && (
            <button
              type="button"
              onClick={() => appendChild(newChild())}
              className={cn(primaryEntityActionButtonClass, 'h-9 gap-1.5 px-3.5 whitespace-nowrap')}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              Add Sub Section
            </button>
          )}
        </div>

        {childFields.length === 0 ? (
          <p className="text-xs text-slate-500">No sub-sections. Add one to edit items.</p>
        ) : (
          <div className="overflow-x-auto rounded border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-[#dfeaf5] text-slate-900 text-xs border-b border-[#c8d7e8]">
                <tr>
                  <th className="px-2 py-2 text-left font-bold w-10" aria-hidden />
                  <th className="px-2 py-2 text-left font-bold">Key</th>
                  {mode === 'missingInfo' ? (
                    <th className="px-2 py-2 text-center font-bold w-24">Has input</th>
                  ) : (
                    <th className="px-2 py-2 text-center font-bold w-28">Internal only</th>
                  )}
                  <th className="px-2 py-2 text-left font-bold">Action</th>
                </tr>
              </thead>
              <tbody>
                {childFields.map((cf, childIndex) => {
                  const childErrors = sectionErrors?.child?.[childIndex]
                  const isLast = childIndex === childFields.length - 1
                  const expanded = expandedChildIndex === childIndex
                  const base = `${fieldName}.child.${childIndex}` as const
                  return (
                    <Fragment key={cf.id}>
                      <tr className="border-b border-[#dfe7f0]">
                        <td className="px-1 py-2 align-middle">
                          <button
                            type="button"
                            onClick={() => toggleExpand(childIndex)}
                            className="p-1 rounded text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-button/30"
                            aria-expanded={expanded}
                            aria-controls={`underwriting-child-rich-${sectionIndex}-${childIndex}`}
                            title={
                              expanded ? 'Hide label and description' : 'Show label and description'
                            }
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" aria-hidden />
                            ) : (
                              <ChevronRight className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                        </td>
                        <td className="px-2 py-2 align-top">
                          <input
                            type="text"
                            placeholder="Key"
                            className="w-full min-w-[120px] rounded border border-slate-300 px-2 py-1 text-sm"
                            {...register(`${fieldName}.child.${childIndex}.key`)}
                          />
                          {childErrors?.key && (
                            <p className="text-xs text-red-600 mt-0.5">{childErrors.key.message}</p>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center align-middle">
                          {mode === 'missingInfo' ? (
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded accent-button"
                              {...register(`${fieldName}.child.${childIndex}.hasInput`)}
                            />
                          ) : (
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded accent-button"
                              {...register(`${fieldName}.child.${childIndex}.isLock`, {
                                setValueAs: (v) => (v ? 1 : 0),
                              })}
                            />
                          )}
                        </td>
                        <td className="px-2 py-2 align-middle whitespace-nowrap">
                          {isLast && (
                            <button
                              type="button"
                              onClick={() => appendChild(newChild())}
                              className={cn(
                                secondaryEntityActionButtonClass,
                                'mr-1 h-8 min-w-0 gap-1 px-2.5 text-xs',
                              )}
                            >
                              <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              Add
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveChild(childIndex)}
                            className={cn(
                              deleteEntityActionButtonClass,
                              'h-8 min-w-0 gap-1 px-2.5 text-xs',
                            )}
                          >
                            <Trash2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            Remove
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr
                          id={`underwriting-child-rich-${sectionIndex}-${childIndex}`}
                          className="border-t border-slate-100 bg-slate-50"
                        >
                          <td colSpan={4} className="px-3 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">
                                  Label
                                </label>
                                <Controller
                                  name={`${base}.label`}
                                  control={control}
                                  render={({ field: { value, onChange } }) => (
                                    <CKEditor4
                                      id={`${base}.label`}
                                      value={value || ''}
                                      onChange={onChange}
                                    />
                                  )}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-700 mb-1">
                                  Description
                                </label>
                                <Controller
                                  name={`${base}.description`}
                                  control={control}
                                  render={({ field: { value, onChange } }) => (
                                    <CKEditor4
                                      id={`${base}.description`}
                                      value={value || ''}
                                      onChange={onChange}
                                    />
                                  )}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
